import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretUp, CaretDown, CaretLeft, CaretRight } from "@phosphor-icons/react";
import "../styles/siloCube.css";
import { cropStyle, FACES, LABELS } from "@/lib/siloCubeFaces";
import { edgeRenderStyle, isCarve } from "@/lib/edgeStyles";
import { loadNavPrefs, subscribeNavPrefs, getNavPrefsCached, applyFlip, isCoarsePointer } from "@/lib/cubeNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Face graph (FACES) + labels (LABELS) live in lib/siloCubeFaces.js, shared with
// the studio's nav test pad.

// SiloCubeCard — the in-place swipeable silo pill. Swiping/clicking happens
// right on the dashboard; nothing here goes full-screen. The image overlays
// the existing pill at its EXACT size. Markers are strictly minimal: a tiny
// "−" (opens the photo-setup tool) and tiny edge carets (click to move).
export function SiloCubeCard({ silo, cubeEnabled, isFounder, onOpenStudio, children, topRightBadge }) {
  const nav = useNavigate();
  const faces = silo?.cube?.faces || {};
  const hasImage = !!faces.center?.image_id;
  const alwaysLabel = !!silo?.cube?.always_show_label;
  const [faceKey, setFaceKey] = useState("center");
  const [tapState, setTapState] = useState(0); // center only: 0 image · 1 name · 2 buttons
  const [showLabel, setShowLabel] = useState(false);

  const idleTimer = useRef(null);
  const tapTimer = useRef(null);
  const tapCount = useRef(0);
  const startPt = useRef(null);
  const moved = useRef(false);
  const bareInteractive = useRef(false);
  const moveRef = useRef(null);
  const wheelAcc = useRef({ x: 0, y: 0, t: 0, cooldown: 0 });
  const wheelCleanup = useRef(null);
  const rootRef = useRef(null);
  const navPrefs = useRef(getNavPrefsCached());
  useEffect(() => {
    loadNavPrefs().then((p) => { navPrefs.current = p; });
    return subscribeNavPrefs((p) => { navPrefs.current = p; });
  }, []);
  // Apply the user's per-input direction flips (set in the studio) before moving.
  const flipFor = (rawDir, ctx, input) => applyFlip(rawDir, navPrefs.current?.[ctx]?.[input]);
  const ctxKey = () => (isCoarsePointer() ? "mobile" : "desktop");

  const cardBase = `bg-white border hairline rounded-xl flex flex-col relative ${
    silo?.suspended ? "border-[#C16E5A]/40 bg-[#FFF7F1]" : ""
  }`;
  // Any non-"clean" edge (frame, mask, fade, vignette…) makes the pill box itself
  // transparent — no leftover rounded rectangle / box-lines behind the artwork or
  // the cut-away areas, so only the page shows through. (Handled inline below.)

  const faceImg = (k) => {
    const id = faces[k]?.image_id || faces.center?.image_id;
    return id ? `${BACKEND_URL}/api/silo-images/${id}` : null;
  };
  const faceEdge = (k) => faces[k]?.edge_style || faces.center?.edge_style || "clean";
  // edge slider strength / variance live on the same face (fall back to center).
  const faceEdgeData = (k) => (faces[k]?.edge_style ? faces[k] : faces.center) || {};
  // the framing (zoom/pan) follows whichever image is actually shown: the face's
  // own image if it has one, otherwise the center image it falls back to.
  const faceCrop = (k) => (faces[k]?.image_id ? faces[k]?.crop : faces.center?.crop) || null;

  const disarmIdle = () => { if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; } };
  const armIdle = () => {
    disarmIdle();
    idleTimer.current = setTimeout(() => { setFaceKey("center"); setTapState(0); }, 10000);
  };
  useEffect(() => () => { disarmIdle(); if (tapTimer.current) clearTimeout(tapTimer.current); }, []);

  // auto-reveal the label on non-center faces after ~0.9s
  useEffect(() => {
    setShowLabel(false);
    if (faceKey === "center") return;
    const t = setTimeout(() => setShowLabel(true), 900);
    return () => clearTimeout(t);
  }, [faceKey]);

  // Jun 2026 (Randy) — clicking AWAY from a silo resets it to MAIN (center cover).
  // Each card watches document pointerdowns (capture, so EdgeZone stopPropagation
  // can't hide it); a press anywhere outside THIS card snaps it home. So clicking
  // empty space — or another silo — returns every other pill to main.
  useEffect(() => {
    if (!cubeEnabled) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setFaceKey("center");
        setTapState(0);
        disarmIdle();
      }
    };
    document.addEventListener("pointerdown", onDocDown, true);
    return () => document.removeEventListener("pointerdown", onDocDown, true);
  }, [cubeEnabled]);

  const goTab = (tabId) => { try { sessionStorage.setItem(`silo_tab:${silo.id}`, tabId); } catch { /* */ } nav(`/dashboard/silo/${silo.id}`); };
  const activateFace = (k) => {
    if (k === "center") return;
    if (k === "assistant") return nav(`/silo/${silo.slug}/assistant`); // Phase 3 — private AI Assistant page
    if (k === "wars") return nav("/wars");
    if (k === "public-qa") return nav(`/s/${silo.slug}`);
    if (k === "fan-portal") return nav("/fan-portal");
    if (k === "account") return nav("/dashboard/account-settings");
    if (k === "admin") return nav("/admin");
    return goTab(k); // tabs + settings
  };

  const move = (dir) => {
    let next = FACES[faceKey]?.[dir];
    // Up/down work from ANY face — if the current face has no up/down neighbor,
    // hop into the up-chain (Fan Portal) or down-chain (Public Q&A).
    if (!next && dir === "up") next = "public-qa";
    if (!next && dir === "down") next = "fan-portal";
    if (!next) return;
    if (next === "admin" && !isFounder) return; // no admin face for non-founders; account is the floor
    setFaceKey(next);
    setTapState(0);
    if (next === "center") disarmIdle(); else armIdle();
  };
  moveRef.current = move;

  // ---- Mouse wheel / trackpad → navigate faces, mirroring the SWIPE concept.
  // Attached as a native non-passive listener (callback ref) so we can
  // preventDefault. HORIZONTAL roll → left/right (tab chain / assistant).
  // VERTICAL roll → up/down, but only when the cursor is in the central column
  // (the left/right strips keep normal page scrolling). Scroll UP → Public Q&A
  // → Account → Admin; scroll DOWN → Fan Portal → Silo Wars (same as swiping).
  const setOverlayRef = useCallback((node) => {
    if (wheelCleanup.current) { wheelCleanup.current(); wheelCleanup.current = null; }
    if (!node) return;
    const onWheelNav = (e) => {
      const now = Date.now();
      const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      // vertical from the side strips → leave normal page scrolling alone.
      if (!horizontal) {
        const rect = node.getBoundingClientRect();
        const relX = (e.clientX - rect.left) / (rect.width || 1);
        if (relX <= 0.2 || relX >= 0.8) return;
      }
      e.preventDefault();
      // ONE roll per continuous gesture. A Magic Mouse / trackpad fling fires a
      // BURST of wheel events (momentum); without this lock the cube double-rolls.
      // The lock only releases after the wheel goes quiet for ~90ms = gesture done.
      const acc = wheelAcc.current;
      if (acc.endTimer) clearTimeout(acc.endTimer);
      acc.endTimer = setTimeout(() => { acc.locked = false; acc.x = 0; acc.y = 0; }, 90);
      if (acc.locked) return;
      if (now - acc.t > 250) { acc.x = 0; acc.y = 0; }
      acc.t = now;
      acc.x += e.deltaX;
      acc.y += e.deltaY;
      const mag = horizontal ? Math.abs(acc.x) : Math.abs(acc.y);
      if (mag < 48) return;
      const raw = horizontal ? (acc.x > 0 ? "right" : "left") : (acc.y > 0 ? "down" : "up");
      acc.x = 0; acc.y = 0;
      const d = flipFor(raw, "desktop", "wheel");
      if (!d) return; // axis turned off
      acc.locked = true;
      moveRef.current && moveRef.current(d);
    };
    node.addEventListener("wheel", onWheelNav, { passive: false });
    wheelCleanup.current = () => node.removeEventListener("wheel", onWheelNav);
  }, []);

  // ---- tap handling: single tap acts; a quick DOUBLE-tap opens the studio
  // (replaces the old long-press, which iOS hijacks on images). ----
  // Jun 2026 (Randy) — ONE click on the MAIN (center) face goes straight into
  // the silo's Library; on any other face it activates that face's destination.
  const singleTapAction = () => {
    if (faceKey === "center") {
      goTab("library"); // one click on main → into the Library
    } else {
      activateFace(faceKey);
    }
  };
  // ---- tap handling: 1 tap → act · 2 taps → studio · 3 taps → original pill view.
  // A trailing timer counts the taps, so the studio/triple actions fire AFTER the
  // tap's own click, and a triple-tap is caught before the single-tap navigates. ----
  const onTap = () => {
    tapCount.current += 1;
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => {
      const n = tapCount.current;
      tapCount.current = 0;
      tapTimer.current = null;
      if (n >= 3) { setFaceKey("center"); setTapState(2); disarmIdle(); }   // triple → original silo pill (Manage / Hide & public buttons)
      else if (n === 2) { disarmIdle(); onOpenStudio && onOpenStudio(); }   // double → studio
      else { singleTapAction(); }                                          // single → act
    }, 360);
  };

  // ---- gesture handling on the image overlay ----
  const onDown = (e) => {
    startPt.current = { x: e.clientX, y: e.clientY };
    moved.current = false;
  };
  const onMove = (e) => {
    if (!startPt.current) return;
    const dx = e.clientX - startPt.current.x;
    const dy = e.clientY - startPt.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) moved.current = true;
  };
  const onUp = (e) => {
    if (startPt.current && moved.current) {
      const dx = e.clientX - startPt.current.x;
      const dy = e.clientY - startPt.current.y;
      if (Math.abs(dx) >= 40 || Math.abs(dy) >= 40) {
        const raw = Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? "left" : "right") : (dy > 0 ? "down" : "up");
        const d = flipFor(raw, "mobile", "swipe");
        if (d) move(d);
        startPt.current = null;
        return;
      }
    }
    startPt.current = null;
    onTap();
  };
  const onCancel = () => { startPt.current = null; };

  // Bare pill (no overlay: imageless silo, or the sticky classic/buttons view).
  // A single tap on a NEUTRAL area runs the single-tap action; a quick
  // double-tap opens the studio. Taps on real buttons pass through untouched.
  const bareDown = (e) => {
    bareInteractive.current = !!(e.target.closest && e.target.closest("button,a,input,select,textarea,[role='button']"));
  };
  const bareUp = () => {
    if (bareInteractive.current) return;
    onTap();
  };

  const resetIdleOnActivity = () => { if (faceKey !== "center" || tapState !== 0) armIdle(); };

  if (!cubeEnabled) {
    return (
      <div className={`${cardBase} p-6`} data-testid={`silo-card-${silo.slug}`}>{topRightBadge}{children}</div>
    );
  }

  const showOverlay = hasImage && (faceKey !== "center" || tapState < 2);
  const overlayImg = faceImg(faceKey);
  const curEdge = faceEdge(faceKey);
  const edgeData = faceEdgeData(faceKey);
  const edgeStyles = edgeRenderStyle(curEdge, edgeData.edge_amount, edgeData.edge_variance);
  const carve = isCarve(curEdge);
  const overlayLabel = faceKey === "center" ? silo.name : LABELS[faceKey];
  const labelVisible = alwaysLabel ? true : (faceKey === "center" ? tapState === 1 : showLabel);

  // Edge tap/swipe zones — each spans ~20% of the pill on its side. The LEFT &
  // RIGHT zones run the FULL height (z-30, on top) so a vertical swipe started in
  // a side strip is swallowed (no up/down nav) — up/down only fire from the
  // central column. Up/down zones are the top/bottom 20% bands.
  const EdgeZone = ({ d, cls, align, icon }) => (
    <button
      type="button"
      data-no-invert
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); const f = flipFor(d, ctxKey(), "arrows"); if (f) move(f); }}
      data-testid={`cube-hash-${d}-${silo.slug}`}
      title={`Move ${d}`}
      className={`absolute ${cls} z-30 flex ${align} text-white/45 hover:text-white/80 transition-colors`}
    >
      {icon}
    </button>
  );

  return (
    <div
      ref={rootRef}
      className={`${showOverlay && carve ? "bg-transparent flex flex-col relative" : cardBase} p-6 ${showOverlay && !carve ? "overflow-hidden" : ""}`}
      data-testid={`silo-card-${silo.slug}`}
      onPointerMove={resetIdleOnActivity}
      onPointerDown={!showOverlay ? bareDown : undefined}
      onPointerUp={!showOverlay ? bareUp : undefined}
    >
      {/* The classic card content stays MOUNTED so the pill keeps its EXACT
          original size (the content is what gives the pill its height). It's only
          made invisible when an image face is shown — visibility:hidden keeps its
          layout/size while preventing its words & box-lines from bleeding through
          faded / cut-away edges. display:contents keeps the flex layout intact. */}
      <div style={{ display: "contents" }} className={showOverlay ? "invisible" : ""}>{children}</div>
      {/* Feb 2026 pt89h (Randy) — top-right badge slot renders OUTSIDE
          the invisible-when-art container so alerts stay visible even
          when silo art is on. Positioned absolutely inside this card;
          the consumer (PendingConfirmationsDot) picks its own inset
          + z-index (z-40, above the art overlay's z-20). */}
      {topRightBadge}

      {/* Jun 2026 (Randy) — discoverability wedge for imageless silos: a small
          white banner straddling the top edge that hints you can double-tap to
          open the image studio. Hint-only (pointer-events-none) so the card's
          own double-tap still fires through it. */}
      {!hasImage && (
        <div
          className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none"
          data-testid={`silo-double-tap-hint-${silo.slug}`}
        >
          <div
            className="bg-white text-[#1C1B1A] shadow-sm flex items-center justify-center px-4"
            style={{
              height: "17px",
              clipPath: "polygon(7% 0, 93% 0, 100% 50%, 93% 100%, 7% 100%, 0 50%)",
              WebkitClipPath: "polygon(7% 0, 93% 0, 100% 50%, 93% 100%, 7% 100%, 0 50%)",
            }}
          >
            <span className="text-[8px] font-medium tracking-tight whitespace-nowrap leading-none">Double tap for image overlay</span>
          </div>
        </div>
      )}

      {showOverlay && (
        <div
          className="absolute inset-0 z-20 select-none cursor-pointer"
          data-testid={`silo-cube-face-${silo.slug}`}
          ref={setOverlayRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onCancel}
          onContextMenu={(e) => e.preventDefault()}
          style={{ touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
        >
          <div className="cube-edge w-full h-full" data-edge={curEdge} style={edgeStyles.container}>
            {overlayImg && <img src={overlayImg} alt={overlayLabel} className="cube-edge-img" draggable={false} style={{ ...cropStyle(faceCrop(faceKey)), ...edgeStyles.img }} />}
            {edgeStyles.overlay && <div className="absolute inset-0 pointer-events-none" data-no-invert style={edgeStyles.overlay} />}
          </div>
          <div
            className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 ${
              labelVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            <span data-no-invert className="font-serif-display text-4xl sm:text-5xl font-semibold text-white text-center px-4 drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]">
              {overlayLabel}
            </span>
          </div>
        </div>
      )}

      {/* edge tap zones (web direction cues) — only over an image */}
      {hasImage && showOverlay && (
        <>
          <EdgeZone d="up" cls="top-0 left-[20%] right-[20%] h-[20%] min-h-[20px]" align="items-center justify-center pt-0.5" icon={<CaretUp size={12} weight="bold" />} />
          <EdgeZone d="down" cls="bottom-0 left-[20%] right-[20%] h-[20%] min-h-[20px]" align="items-center justify-center pb-0.5" icon={<CaretDown size={12} weight="bold" />} />
          <EdgeZone d="left" cls="left-0 inset-y-0 w-[20%] min-w-[20px]" align="items-center justify-start pl-0.5" icon={<CaretLeft size={12} weight="bold" />} />
          <EdgeZone d="right" cls="right-0 inset-y-0 w-[20%] min-w-[20px]" align="items-center justify-end pr-0.5" icon={<CaretRight size={12} weight="bold" />} />
        </>
      )}
    </div>
  );
}
